import { NavLink, useNavigate } from 'react-router-dom';
import { BarChart3, User, School, Radio, LogOut, FileDown, ClipboardList, ListChecks, Home } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/api/endpoints/auth';
import { ORGANIZER_BASE, ORGANIZER_LOGIN } from '@/config/routes';
import { SidebarBrand } from './SidebarBrand';
import toast from 'react-hot-toast';

const navItems: { to: string; label: string; icon: LucideIcon }[] = [
  { to: `${ORGANIZER_BASE}`, label: 'Instrukcja', icon: Home },
  { to: `${ORGANIZER_BASE}/live`, label: 'Na żywo test teor.', icon: Radio },
  { to: `${ORGANIZER_BASE}/session-status`, label: 'Status testu E2 teor.', icon: ListChecks },
  { to: `${ORGANIZER_BASE}/practical-scores`, label: 'Wyniki praktyczne E2', icon: ClipboardList },
  { to: `${ORGANIZER_BASE}/leaderboard`, label: 'Tabela wynikow', icon: BarChart3 },
  { to: `${ORGANIZER_BASE}/reports`, label: 'Raporty CSV', icon: FileDown },
  { to: `${ORGANIZER_BASE}/participants`, label: 'Uczestnicy', icon: User },
  { to: `${ORGANIZER_BASE}/schools`, label: 'Szkoły', icon: School },
];

export const OrganizerSidebar = () => {
  const { sidebarOpen } = useUIStore();
  const clearAuthenticated = useAuthStore((s) => s.clearAuthenticated);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors – clear state and redirect regardless
    }
    clearAuthenticated();
    navigate(ORGANIZER_LOGIN, { replace: true });
    toast.success('Wylogowano pomyślnie');
  };

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-gray-200 shadow-sm transition-all duration-300',
        sidebarOpen ? 'w-64' : 'w-16',
      )}
    >
      <SidebarBrand sidebarOpen={sidebarOpen} />

      {sidebarOpen && (
        <div className="mx-2 mt-3 rounded-md bg-competition-accent/15 px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-competition-accent-dark">
            Panel Organizatora
          </p>
        </div>
      )}

      <nav className="flex-1 space-y-1 overflow-y-auto p-2 mt-1">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === ORGANIZER_BASE}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-competition-accent/10 hover:text-competition-accent-dark',
              )
            }
          >
            <Icon className="h-5 w-5 shrink-0" />
            {sidebarOpen && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-gray-200 p-2">
        <button
          onClick={handleLogout}
          className={cn(
            'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700',
          )}
          title="Wyloguj"
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {sidebarOpen && <span>Wyloguj</span>}
        </button>
      </div>
    </aside>
  );
};
